import { Dispatch, SetStateAction } from 'react';
import { useParams } from 'react-router-dom';

import { useMutation, useQuery } from 'react-query';
import { toast } from 'react-toastify';

import { GroupFormInputs } from '@type/group';

import { useInternalRouter } from '@hooks/useInternalRouter';
import useQueryString from '@hooks/useQueryString';

import {
  CREATE_ERROR_MESSAGE,
  CREATE_SUCCESS_MESSAGE,
  DELETE_ERROR_MESSAGE,
  DELETE_SUCCESS_MESSAGE,
  EDIT_ERROR_MESSAGE,
  EDIT_SUCCESS_MESSAGE,
} from '@constants/toastMessage';

import { bizNumberFormat, corparteNumberFormat } from '@utils/utils';

import { create, getList, getOne, remove, update } from '@apis/api';

/** 마스터 계정 > 회사 관리 > 목록 조회 */
export function useClientList() {
  const { pageNo, pageSize } = useQueryString();
  const { data, isLoading } = useQuery(['client-list', pageNo, pageSize], () =>
    getList(`/company?page=${pageNo}&size=${pageSize}`, '')
  );

  return {
    data: data?.content || [],
    totalCount: data?.totalCount || 0,
    isLoading,
  };
}

/** 마스터 계정 > 회사 관리 > 상세 */
export function useClientDetail() {
  const { groupId } = useParams();
  const { data, isLoading } = useQuery(['client-detail', groupId], () =>
    getOne('/company', groupId)
  );

  return {
    data: {
      ...data,
      corporateNo: corparteNumberFormat(data?.corporateNo),
      bizNo: bizNumberFormat(data?.bizNo),
    },
    isLoading,
  };
}

/** 마스터 계정 > 화시 관리 > 상세 > 삭제하기 */
export function useClientDelete(
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>
) {
  const router = useInternalRouter();
  const { groupId } = useParams();
  const { mutate, isLoading } = useMutation(
    ['client-delete', groupId],
    () => remove(`/company/${groupId}`),
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(DELETE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
          setConfirmModalOpen(false);
        } else {
          toast.success(DELETE_SUCCESS_MESSAGE);
          setConfirmModalOpen(false);
          router.replace('/client');
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 마스터 계정 > 회사 관리 > 상세 > 수정하기 */
export function useClientUpdate(
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>
) {
  const router = useInternalRouter();
  const { groupId } = useParams();

  const { mutate, isLoading } = useMutation(
    ['client-update', groupId],
    (value: GroupFormInputs) => update(`/company/${groupId}`, value),
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(EDIT_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
          setConfirmModalOpen(false);
        } else {
          toast.success(EDIT_SUCCESS_MESSAGE);
          setConfirmModalOpen(false);
          router.replace(`/client/${groupId}`);
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}

/** 마스터 계정 > 회사 관리 > 상세 > 등록하기 */
export function useClientCreate(
  setConfirmModalOpen: Dispatch<SetStateAction<boolean>>
) {
  const router = useInternalRouter();
  const { mutate, isLoading } = useMutation(
    ['client-create'],
    (value: GroupFormInputs) => create('/company', value),
    {
      onSuccess: (res: any) => {
        if (res?.isError) {
          toast.error(CREATE_ERROR_MESSAGE);
          console.log(`ERROR[${res?.data?.errorCode}] : ${res?.data?.message}`);
          setConfirmModalOpen(false);
        } else {
          toast.success(CREATE_SUCCESS_MESSAGE);
          setConfirmModalOpen(false);
          router.replace('/client');
        }
      },
    }
  );

  return {
    mutate,
    isLoading,
  };
}
